import { Pipe, ChangeDetectorRef, inject } from '@angular/core';
import { TitleCasePipe } from '@angular/common';
import { SgToNoCasePipe } from '@sg/ui-kit/core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

/**
 * A special "translate" pipe for columns in pbl-ngrid that behaves similar to `TranslatePipe` with a special fallback when translation fail.
 * `sgTranslateColumn` accepts a prefix that will be added to the query before trying to translate it, if the modified query translation failed
 * it will use the original query as the source and pass it through the pipes `sgToNoCase` and then `titlecase`.
 *
 * When you want to pass translate parameters, setting the prefix is mandatory, even if no prefix is actually used (e.g. '').
 *
 * For example:
 *
 * Using the following translation dictionary:
 *
 * ```json
 * {
 *   "HOME": {
 *       "HELLO": "hello world"
 *       "HELLO_PARAM": "hello {{value}}"
 *   }
 * }
 * ```
 *
 *   - The expression `{{ 'HELLO' | sgTranslateColumn: 'HOME' }}` will first try to find a translation for the expression + prefix, that is
 * `HOME.HELLO`, which will return a VALID value thus the result will be `hello world`
 *
 *   - The expression `{{ 'HELLO_PARAM' | sgTranslateColumn: 'HOME':'ANGULAR' }}` will first try to find a translation for the expression + prefix, that is
 * `HOME.HELLO` and also pass the parameters which will return a VALID value thus the result will be `hello ANGULAR`
 *
 *   - The expression `{{ 'NOT_THERE' | sgTranslateColumn: 'HOME' }}` will first try to find a translation for the expression + prefix, that is
 * `HOME.NOT_THERE`, which will return an INVALID value. It will now use internal logic and return `Not There`.
 *
 * The default behaviour is identical to `TranslatePipe`, if the translate pipe found a match and returned a different (translated) value
 * that value will be used, making `sgTranslateColumn` a passthrough pipe.
 *
 * When a translation is not found the pipe will treat the input value as an X-Case string, e.g. CamelCase, SnakeCase, and will string the
 * case format into a space delimited format and then pipe that value against the titlecase pipe.
 * For example: given the input "johnDoe" returns "Jhon Doe"
 */
@Pipe({
  name: 'sgTranslateColumn',
  standalone: false,
})
export class SgTranslateColumnPipe extends TranslatePipe {
  // inject() instead of constructor params so SgToNoCasePipe isn't read at class-decoration
  // time — that breaks the @sg/common ⇄ @sg/ui-kit/core barrel cycle that crashed Storybook
  // ("Cannot read properties of undefined (reading 'SgToNoCasePipe')").
  private titleCasePipe = inject(TitleCasePipe);
  private noCasePipe = inject(SgToNoCasePipe);

  constructor(translate: TranslateService, ref: ChangeDetectorRef) {
    super(translate, ref);
  }

  transform(query: string, prefix: string, ...args: any[]): string {
    query = query || '';
    const modifiedQuery = prefix ? `${prefix}.${query}` : query;

    const result = super.transform(modifiedQuery, ...args);
    if (result === modifiedQuery) {
      return this.titleCasePipe.transform(this.noCasePipe.transform(query));
    } else {
      return result;
    }
  }
}
